import { useSDK, useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { iExternalAppConfig_Asana } from "@sdk/user-management/user-management.models";
import { Alert, Button, Form, Select, Spin, Tag, Tooltip, message } from "antd";
import { ExternalAuthConnector } from "components/common/external-auth-connector-button/external-auth-connector-button";
import {
  LoadingIndicatorWithSpin,
  LoadingIndicatorWithoutSpin,
} from "components/common/loading-indicator/loading-indicator";
import { HelpUsHelpYou } from "components/help-us-help-you/help-us-help-you";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "store/modules/ui-state/ui-state.selectors";
import { selectIntegrationConfig_Asana } from "store/modules/workspace/workspace.selectors";

export const AsanaIntegrationConfig = () => {
  const integrationConfig = useSelector(selectIntegrationConfig_Asana);
  const isDarkMode = useSelector(selectIsDarkMode);

  const { doAction: completeIntegration, isProcessing: isAdding } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (code: string) =>
          SDK.externalAccounts.completeIntegration({
            type: "ASANA",
            data: {
              code,
            },
          }),
        successMessage: "Integration has been successful",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [],
    );

  const { doAction: removeIntegration, isProcessing: isRemoving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.externalAccounts.removeIntegration({
            type: "ASANA",
          }),
        successMessage: "Integration has been removed",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [],
    );

  const { doAction: saveConfig, isProcessing: isSavingConfig } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (config) =>
          SDK.externalAccounts.updateConfig({
            type: "ASANA",
            config,
          }),
        successMessage: "Configuration has been saved",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [],
    );

  const [form] = Form.useForm();

  const [isConfigEditMode, setConfigEditMode] = useState(false);
  const [formValues, setFormValues] = useState(
    integrationConfig?.config || ({} as iExternalAppConfig_Asana),
  );

  useEffect(() => {
    if (integrationConfig?.config) {
      setFormValues(integrationConfig?.config);
    }
  }, [integrationConfig?.config]);

  const { data: workspaceOptions, isLoading: isWorkspacesLoading } = useSDK(
    (SDK) =>
      SDK.asana.getAvailableWorkspaces({}).then((workspacesRes) => {
        return (workspacesRes?.data || []).map((item) => ({
          value: item.gid,
          label: item.name,
        }));
      }),
    [integrationConfig],
    !integrationConfig || !isConfigEditMode,
    [],
  );

  const { data: projectOptions, isLoading: isProjectsLoading } = useSDK(
    (SDK) =>
      SDK.asana
        .getAvailableProjects({ workspaceId: formValues.workspaceId! })
        .then((projectsRes) => {
          return (projectsRes?.data || []).map((item) => ({
            value: item.gid,
            label: item.name,
          }));
        }),
    [integrationConfig],
    !formValues.workspaceId || !isConfigEditMode,
    [],
  );

  const onSaveConfig = useCallback(
    (e) => {
      e.preventDefault();
      const process = async () => {
        try {
          await form.validateFields();
          const formValues = form.getFieldsValue();
          const selectedWorkspace = workspaceOptions.find(
            (item) => item.value === formValues.workspaceId,
          );
          const selectedProject = projectOptions.find(
            (item) => item.value === formValues.projectId,
          );
          saveConfig({
            ...formValues,
            isReady: true,
            workspaceName: selectedWorkspace?.label,
            projectName: selectedProject?.label,
          } as iExternalAppConfig_Asana).then((d) => {
            setConfigEditMode(false);
          });
        } catch (e) {
          message.error("Please check your input");
        }
      };
      process();
    },
    [form, projectOptions, saveConfig, workspaceOptions],
  );

  const {
    data: test,
    isLoading: isTestingIntegration,
    error,
    reload: reTestIntegration,
  } = useSDK(
    (SDK) => SDK.asana.getAvailableWorkspaces({ searchTerm: "" }),
    [integrationConfig?.config?.isReady],
    !integrationConfig?.config?.isReady,
  );
  useEffect(() => {
    if (integrationConfig && !integrationConfig.config.isReady) {
      setConfigEditMode(true);
    }
  }, [integrationConfig]);

  return (
    <>
      {integrationConfig && (
        <div>
          {integrationConfig.config.isReady ? (
            <Spin
              spinning={isTestingIntegration}
              indicator={<LoadingIndicatorWithSpin />}
            >
              {!error && (
                <Alert
                  message="Integration is active"
                  type="success"
                  showIcon
                />
              )}
              {error && (
                <Alert
                  message="Integration is not working correctly. Try Re-Authenticating"
                  type="error"
                  showIcon
                />
              )}
            </Spin>
          ) : (
            <Alert
              message="Asana integration is not ready yet. Complete the below steps to enable it"
              type="warning"
              showIcon
            />
          )}
          <div className="flex flex-row justify-between mt-8">
            <div className="text">Connected User</div>
            <div className="flex flex-row items-center">
              <div className="">
                <Tag>{integrationConfig.config.userName}</Tag>
              </div>
              <Tooltip title="Remove Integration">
                <Button
                  type="link"
                  icon={<i className="ri-delete-bin-line"></i>}
                  onClick={() => {
                    removeIntegration();
                  }}
                  loading={isRemoving}
                ></Button>
              </Tooltip>
            </div>
          </div>
          <div className="flex flex-row justify-between my-2">
            <div className="text">Default Project</div>
            <div className="flex flex-row items-center">
              <div className="">
                <Tag>
                  {integrationConfig.config.workspaceName ||
                    "Not Workspace Selected"}
                </Tag>
                <Tag>
                  {integrationConfig.config.projectName ||
                    "Not Project Selected"}
                </Tag>
              </div>
              {!isConfigEditMode && (
                <>
                  <Tooltip title="Configure">
                    <Button
                      type="link"
                      icon={<i className="ri-pencil-line"></i>}
                      onClick={() => {
                        setConfigEditMode(true);
                      }}
                      loading={isRemoving}
                    ></Button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
          {isConfigEditMode && (
            <div className=" w-full">
              <Form
                form={form}
                layout="vertical"
                initialValues={integrationConfig.config}
                requiredMark={false}
                className="bold-form-labels"
                onFinish={onSaveConfig}
                onValuesChange={() => {
                  const formValues = form.getFieldsValue();
                  setFormValues(formValues);
                }}
              >
                <Form.Item
                  name="workspaceId"
                  label="Workspace"
                  rules={[
                    {
                      required: true,
                      message: "Please select a workspace",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Workspace"
                    options={workspaceOptions}
                    showSearch
                    loading={isWorkspacesLoading}
                  />
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const workspaceId = getFieldValue(["workspaceId"]);
                    if (!workspaceId) {
                      return <></>;
                    }
                    return (
                      <Form.Item
                        name="projectId"
                        label="Project"
                        rules={[
                          {
                            required: true,
                            message: "Please select a Project",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select Project"
                          options={projectOptions}
                          showSearch
                          loading={isProjectsLoading}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                <div className="flex flex-row justify-end items-center">
                  <Button
                    type="primary"
                    className="font-bold"
                    loading={isAdding}
                    onClick={onSaveConfig}
                  >
                    Save
                  </Button>
                </div>
              </Form>
            </div>
          )}
          <HelpUsHelpYou
            chatMessage="How can we improve Asana Integration for you?"
            emoji="☝️"
            description="Share your workflows with Asana and we'll ensure that our Asana Integration saves you time."
          />
        </div>
      )}

      {!integrationConfig && (
        <div className="flex flex-row justify-center items-center">
          <Spin
            spinning={isAdding}
            indicator={<LoadingIndicatorWithoutSpin />}
            wrapperClassName="flex fill-spinner"
          >
            <ExternalAuthConnector
              type="ASANA"
              onAuthenticated={({ code }) => {
                completeIntegration(code);
              }}
              label="Connect Asana"
            />
          </Spin>
        </div>
      )}
    </>
  );
};
